import React, { useRef, useState, useEffect } from 'react'
import { Mic, Square, Upload, Loader2 } from 'lucide-react'
import { uploadSectionAudio } from '../api/lessonBuilderApi'

// Hai lựa chọn audio KHÔNG dùng AI (không trừ điểm), đặt cạnh nút "Tạo Audio" (TTS OpenAI):
//  • 🎤 Ghi âm   — thu trực tiếp qua micro (MediaRecorder), bấm lại để dừng → tự tải lên.
//  • ⬆️ Tải lên  — chọn file audio (mp3/m4a/...) có sẵn.
// Cả hai gọi /lesson-builder/voice/upload rồi đặt audioUrl cho phần tương ứng.
interface Props {
  lessonId: string
  sectionId: string
  onAudio: (patch: { audioUrl: string }) => void
  disabled?: boolean
  className?: string
}

const AudioSourceButtons: React.FC<Props> = ({ lessonId, sectionId, onAudio, disabled, className }) => {
  const [busy, setBusy] = useState(false)
  const [recording, setRecording] = useState(false)
  const [secs, setSecs] = useState(0)
  const [msg, setMsg] = useState<string | null>(null)
  const recRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const timerRef = useRef<number | null>(null)

  useEffect(() => () => { if (timerRef.current) window.clearInterval(timerRef.current); recRef.current?.stream?.getTracks().forEach((t) => t.stop()) }, [])

  const doUpload = async (blob: Blob, filename: string) => {
    // Không cần lưu bài trước: lesson.id luôn có sẵn (client tự sinh) và được giữ nguyên khi lưu,
    // nên file rơi đúng thư mục bài; audioUrl được giữ lại khi GV bấm Lưu.
    setBusy(true); setMsg(null)
    try {
      const r = await uploadSectionAudio(lessonId, sectionId, blob, filename)
      onAudio({ audioUrl: r.url })
      setMsg('✅ Đã thêm audio. Nhớ Lưu bài.')
    } catch (e: any) {
      setMsg(`Lỗi: ${e?.message || 'không tải được audio'}`)
    } finally { setBusy(false) }
  }

  const startRec = async () => {
    setMsg(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream)
      chunksRef.current = []
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data) }
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop())
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' })
        const ext = (mr.mimeType || '').includes('ogg') ? 'ogg' : 'webm'
        await doUpload(blob, `ghi-am.${ext}`)
      }
      mr.start()
      recRef.current = mr
      setRecording(true); setSecs(0)
      timerRef.current = window.setInterval(() => setSecs((s) => s + 1), 1000)
    } catch {
      setMsg('Không truy cập được micro. Hãy cho phép quyền micro cho trang.')
    }
  }

  const stopRec = () => {
    if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null }
    recRef.current?.stop()
    setRecording(false)
  }

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('audio/')) { setMsg('Vui lòng chọn file âm thanh.'); return }
    await doUpload(file, file.name || 'audio')
  }

  const mm = String(Math.floor(secs / 60)).padStart(2, '0')
  const ss = String(secs % 60).padStart(2, '0')

  return (
    <span className={`inline-flex items-center gap-1.5 flex-wrap ${className || ''}`}>
      {recording ? (
        <button type="button" onClick={stopRec}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-600 text-white text-[11px] font-bold hover:bg-red-700 animate-pulse">
          <Square size={11} /> Dừng {mm}:{ss}
        </button>
      ) : (
        <button type="button" onClick={startRec} disabled={disabled || busy}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-rose-100 text-rose-700 text-[11px] font-bold hover:bg-rose-200 disabled:opacity-50">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Mic size={11} />} Ghi âm
        </button>
      )}
      <label className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-sky-100 text-sky-700 text-[11px] font-bold hover:bg-sky-200 cursor-pointer ${(disabled || busy || recording) ? 'opacity-50 pointer-events-none' : ''}`}>
        <Upload size={11} /> Tải lên
        <input type="file" accept="audio/*" className="hidden" onChange={onPickFile} disabled={disabled || busy || recording} />
      </label>
      {msg && <span className="text-[10px] text-gray-600">{msg}</span>}
    </span>
  )
}

export default AudioSourceButtons
